/** @format */

import { useState, useCallback } from 'react';
import * as vendorService from '../services/vendorsService';
import { VendorFormInput } from '../types';

interface UseVendorFormReturn {
  vendors: VendorFormInput[];
  loading: boolean;
  error: string | null;
  success: boolean;
  addVendor: () => void;
  removeVendor: (index: number) => void;
  updateVendor: (index: number, globalName: string) => void;
  submitVendors: () => Promise<boolean>;
  resetForm: () => void;
  clearMessage: () => void;
}

/**
 * Custom hook for vendor form state management
 */
export const useVendorForm = (): UseVendorFormReturn => {
  const [vendors, setVendors] = useState<VendorFormInput[]>([{ globalName: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const addVendor = useCallback(() => {
    setVendors((prev) => [...prev, { globalName: '' }]);
  }, []);

  const removeVendor = useCallback((index: number) => {
    setVendors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateVendor = useCallback((index: number, globalName: string) => {
    setVendors((prev) => {
      const newVendors = [...prev];
      newVendors[index] = { globalName };
      return newVendors;
    });
  }, []);

  const resetForm = useCallback(() => {
    setVendors([{ globalName: '' }]);
    setError(null);
    setSuccess(false);
  }, []);

  const submitVendors = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Filter out empty vendor names
      const validVendors = vendors.filter((v) => v.globalName.trim() !== '');

      if (validVendors.length === 0) {
        setError('Please add at least one vendor');
        setLoading(false);
        return false;
      }

      // Use batch endpoint even for single vendor
      await vendorService.batchCreateVendors(validVendors);
      setSuccess(true);
      resetForm();
      return true;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error?.error ||
        'Failed to submit vendors';
      setError(errorMessage);
      console.error('Error submitting vendors:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [vendors, resetForm]);

  const clearMessage = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    vendors,
    loading,
    error,
    success,
    addVendor,
    removeVendor,
    updateVendor,
    submitVendors,
    resetForm,
    clearMessage,
  };
};
